<template>
  <div>
    <search-product @submit="submitSearch" />

    <div class="iotplt-table-operation-buttons">
      <a-space>
        <a-button
          type="primary"
          @click="showImportModal"
          v-if="isShowMenu('customer/agents_products/agency/by_top_agent/import')"
        >导入套餐
        </a-button>

        <update-product-effective
          v-if="isShowMenu('customer/agents_products/agency/by_top_agent/effective')"
          :agent-id="agentId"
          :selected-row-keys="selectedRowKeys"
          @completed="fetchData"
        />
      </a-space>
    </div>

    <a-table
      size="middle"
      :columns="columns"
      :data-source="data"
      :loading="loading"
      :scroll="{ x: 'max-content' }"
      :pagination="false"
      @change="sortChange"
      :row-selection="{ selectedRowKeys: selectedRowKeys, onChange: onSelectChange }"
      row-key="id"
    >
      <div slot="name" slot-scope="name, record">
        <a v-if="isHasShowPermission" @click="showInfoModal(record.id)"> {{ name }} </a>
        <span v-else> {{ name }} </span>
      </div>

      <span slot="price" slot-scope="text" :class="{ 'pending-set': text === '待设置'}">
        {{ text }}
      </span>

      <div slot="retail_price" slot-scope="retail_price">
        <span>{{ formatFloatDisplay(retail_price) }}</span>
      </div>

      <span slot="actions" slot-scope="text, record">
        <a-dropdown>
          <a class="ant-dropdown-link" @click="e => e.preventDefault()">
            操作 <a-icon type="down" />
          </a>
          <a-menu slot="overlay">
            <a-menu-item v-if="isHasEditPermission">
              <a @click="showEditModal(record.id)">编辑</a>
            </a-menu-item>
            <a-menu-item>
              <a @click="showOperationHistoryModal(record.id)">操作历史</a>
            </a-menu-item>
          </a-menu>
        </a-dropdown>
      </span>
    </a-table>

    <pagination
      v-show="pagination.total_count > 0"
      :total-count="pagination.total_count"
      :page.sync="query.page"
      :per-page.sync="query.per_page"
      @change="fetchData"
    />

    <!-- 导入模态框 -->
    <import-agents-product
      v-if="isShowImportModal"
      :agent-id="agentId"
      :visible.sync="isShowImportModal"
      @completed="fetchData"
    />

    <!-- 编辑模态框 -->
    <edit-agents-product
      v-if="isShowEditModal"
      :visible.sync="isShowEditModal"
      :id="editingId"
      @completed="fetchData"
    />

    <!-- 详情模态框 -->
    <show-agents-product
      v-if="isShowInfoModal"
      :visible.sync="isShowInfoModal"
      :id="showingId"
    />

    <operation-history-list-modal
      v-if="isShowOperationHistoryModal"
      :reference-id="showingOperationHistoryId"
      reference-type="AgentsProduct"
      :visible.sync="isShowOperationHistoryModal"
    />
  </div>
</template>

<script>
import { hasPermission } from '@/utils/permission'
import { findAgencyAgentsProductsByTopAgent } from '@/api/agents_product'
import SearchProduct from '@/views/agents_products/top_agent_view_agency/Search'
import Pagination from '@/components/Pagination'
import { formatFloat } from '@/utils/filter'

export default {
  name: 'AgentsProductList',
  components: {
    Pagination,
    SearchProduct,
    EditAgentsProduct: () => import('@/views/agents_products/top_agent_view_agency/Edit'),
    ShowAgentsProduct: () => import('@/views/agents_products/top_agent_view_agency/Show'),
    OperationHistoryListModal: () => import('@/views/operation_histories/Modal'),
    ImportAgentsProduct: () => import('@/views/agents_products/top_agent_view_agency/import/index'),
    UpdateProductEffective: () => import('@/views/agents_products/top_agent_view_agency/UpdateEffective')
  },
  data() {
    return {
      data: [],
      loading: true,
      isShowEditModal: false, // 是否显示编辑表单弹窗
      isShowInfoModal: false, // 是否显示详情弹窗
      editingId: 0, // 正在编辑的记录id
      showingId: 0,
      isShowOperationHistoryModal: false,
      showingOperationHistoryId: 0,
      isShowImportModal: false,
      selectedRowKeys: [],
      pagination: {
        total_count: 0
      },
      query: {
        effective: '1',
        per_page: 10
      },
      sort: {
        sort_field: '',
        sort_order: ''
      }
    }
  },
  created() {
    this.fetchData()
  },
  computed: {
    agentId() {
      return parseInt(this.$route.params.id)
    },

    agentType() {
      return parseInt(this.$store.getters.userAgentType)
    },

    columns() {
      var columnArr = [
        {
          title: '套餐编号',
          dataIndex: 'code',
          fixed: 'left'
        },
        {
          title: '客户套餐名称',
          dataIndex: 'name',
          scopedSlots: { customRender: 'name' },
          fixed: 'left'
        },
        {
          title: '套餐名称',
          dataIndex: 'top_agent_product_name',
          fixed: 'left'
        },
        {
          title: '套餐类型',
          dataIndex: 'product_type'
        },
        {
          title: '运营商种类',
          dataIndex: 'carrier_type'
        },
        {
          title: '用户业务模式',
          dataIndex: 'user_business_type'
        },
        {
          title: '套餐周期',
          dataIndex: 'service_cycle'
        },
        {
          title: '套餐容量',
          dataIndex: 'package_capacity'
        },
        {
          title: '语音',
          dataIndex: 'voice_capacity'
        },
        {
          title: '套餐价格(元)',
          dataIndex: 'top_agent_distributor_price',
          customRender: formatFloat
        },
        {
          title: '客户价格(元)',
          dataIndex: 'distributor_price',
          scopedSlots: { customRender: 'price' }
        },
        {
          title: '套餐有效性',
          dataIndex: 'effective'
        },
        {
          title: '关联卡数',
          dataIndex: 'card_count',
          sorter: true,
          sortOrder: this.sort.sort_field === 'card_count' ? this.sort.sort_order : false
        },
        {
          title: '导入时间',
          dataIndex: 'created_at',
          sorter: true,
          sortOrder: this.sort.sort_field === 'created_at' ? this.sort.sort_order : false
        },
        {
          title: '操作',
          dataIndex: 'actions',
          scopedSlots: { customRender: 'actions' },
          fixed: 'right'
        }
      ]

      if (this.agentType === 1) {
        columnArr.splice(8, 0, {
          title: '零售价格(元)',
          dataIndex: 'retail_price',
          scopedSlots: { customRender: 'retail_price' }
        })
      }

      return columnArr
    },

    isHasEditPermission() {
      return hasPermission('customer/agents_products/agency/by_top_agent/edit')
    },

    isHasShowPermission() {
      return hasPermission('customer/agents_products/agency/by_top_agent/show')
    }
  },
  methods: {
    onSelectChange(selectedRowKeys) {
      this.selectedRowKeys = selectedRowKeys
    },

    isShowMenu(menu) {
      return hasPermission(menu)
    },

    showEditModal(recordId) {
      this.editingId = recordId
      this.isShowEditModal = true
    },

    showInfoModal(recordId) {
      this.showingId = recordId
      this.isShowInfoModal = true
    },

    // 显示操作历史
    showOperationHistoryModal(id) {
      this.showingOperationHistoryId = id
      this.isShowOperationHistoryModal = true
    },

    showImportModal() {
      this.isShowImportModal = true
    },

    submitSearch(search) {
      // 重置排序
      this.sort = this.$options.data().sort
      // 赋值搜索条件，保留 per_page
      this.query = Object.assign({},
        this.$options.data().query,
        { per_page: this.query.per_page },
        search)
      this.fetchData()
    },

    formatFloatDisplay(value) {
      if (value === -1) {
        return '-'
      }

      return formatFloat(value)
    },

    sortChange(pagination, filters, sorter) {
      this.sort.sort_field = sorter.field
      this.sort.sort_order = sorter.order
      // 跳转第一页
      this.query.page = 1
      this.fetchData()
    },

    fetchData() {
      this.loading = true
      this.selectedRowKeys = []
      findAgencyAgentsProductsByTopAgent(
        Object.assign({ agent_id: this.agentId }, this.query, this.sort)
      ).then((res) => {
        if (res.code === 0) {
          this.data = res.data
          this.pagination = res.pagination
        }
        this.loading = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.pending-set {
  color: red;
}
</style>
